import styled from "styled-components";

const BORDER = "#ddd";
const INPUT_BORDER = "#c5c6ca";
const TEXT_DARK = "#19191b";
const TEXT_MUTED = "#474749";
const DANGER = "#d0021b";

/** Figma form column width (the order-summary panel sits beside it at this width). */
const COLUMN_MAX_WIDTH = 770;

export const StepLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
`;

export const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: ${COLUMN_MAX_WIDTH}px;
  padding: 40px;
  background: var(--color-white-100, #fff);
  border: 1px solid ${BORDER};
  border-radius: 8px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${TEXT_DARK};
`;

/* ---- Payment method picker ---- */

export const MethodGrid = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

export const MethodCard = styled.button<{ $active: boolean }>`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  min-width: 0;
  padding: 10px 10px 10px 15px;
  /* Per Figma: every card has the same #ddd border; the selected card is white
     + shadow + full opacity, unselected cards are muted bg + opacity 0.6.
     They are still clickable (pointer) — only the not-allowed cursor was wrong. */
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? "#fff" : "#f8f8f8")};
  box-shadow: ${({ $active }) =>
    $active ? "0 1px 2px 0 rgba(0, 0, 0, 0.05)" : "none"};
  opacity: ${({ $active }) => ($active ? 1 : 0.6)};
  cursor: pointer;
  text-align: left;

  /* not-allowed only when a method is actually disabled (native disabled attr). */
  &:disabled {
    cursor: not-allowed;
  }
`;

export const MethodIcon = styled.img`
  /* Figma sizes each glyph at its natural proportion within a 24px-tall slot
     (e.g. the Direct Debit wordmark is 45×14), so cap height rather than
     forcing 24 — forcing it scaled the wide marks up (DD became ~77px). */
  display: block;
  height: auto;
  max-height: 24px;
  width: auto;
`;

export const MethodLabel = styled.span<{ $active: boolean }>`
  font-family: Lato, sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${({ $active }) => ($active ? TEXT_DARK : TEXT_MUTED)};
`;

/* ---- Card (Stripe) form ---- */

export const CardFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const CardFieldRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

export const StripeField = styled.div<{ $error?: boolean }>`
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  align-items: center;
  /* min-height (not height): as a column child its flex-basis is 0, which would
     otherwise collapse the explicit height to the Stripe iframe's natural ~21px. */
  min-height: 44px;
  padding: 0 16px;
  background: #fff;
  border: 1px solid ${({ $error }) => ($error ? DANGER : INPUT_BORDER)};
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  .StripeElement {
    width: 100%;
  }
`;

/* ---- Address form ---- */

export const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const FieldRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;

export const Field = styled.div`
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
`;

export const ErrorText = styled.p`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  font-family: Lato, sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: ${DANGER};
`;

/* ---- Same-address checkbox ---- */

export const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: ${TEXT_DARK};
`;
